import { Component } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { delay, map, of } from 'rxjs';
import {
  ButtonComponent,
  DialogRefService,
  InputComponent,
  SearchSelectComponent,
  SelectComponent,
} from '@group24ag/flowkit';

interface Manager {
  id: number;
  name: string;
  department: string;
}

@Component({
  selector: 'app-dialog-edit-profile',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    SearchSelectComponent,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <ng-template #deptSelectedTpl let-item="item">{{ item }}</ng-template>
      <ng-template #deptOptionTpl let-option="option">{{ option }}</ng-template>
      <ng-template #managerSelectedTpl let-item="item">
        {{ item.name }}
      </ng-template>
      <ng-template #managerOptionTpl let-option="option">
        <div class="flex flex-col">
          <span>{{ option.name }}</span>
          <span class="text-dune-400 text-xs">{{ option.department }}</span>
        </div>
      </ng-template>

      <div class="grid grid-cols-2 gap-4">
        <flk-input
          label="First name"
          placeholder="First name"
          [formControl]="firstNameControl" />
        <flk-input
          label="Last name"
          placeholder="Last name"
          [formControl]="lastNameControl" />
      </div>

      <flk-input
        label="Email"
        placeholder="name@example.com"
        [type]="'text'"
        [formControl]="emailControl" />

      <flk-select
        label="Department"
        placeholder="Select department"
        [options]="departments"
        [selectedTemplate]="deptSelectedTpl"
        [optionTemplate]="deptOptionTpl"
        [formControl]="departmentControl" />

      <flk-search-select
        label="Manager"
        placeholder="Search for a manager"
        [fetch]="fetchManagers"
        [selectedTemplate]="managerSelectedTpl"
        [optionTemplate]="managerOptionTpl"
        [compareWith]="compareManagers"
        [formControl]="managerControl" />

      <div class="flex items-center justify-between pt-2">
        <button flkButton [appearance]="'ghost'" (click)="onExpand()">
          Expand
        </button>
        <div class="flex gap-2">
          <button
            flkButton
            [appearance]="'secondary'"
            [variant]="'default'"
            (click)="onCancel()">
            Cancel
          </button>
          <button
            flkButton
            [disabled]="
              firstNameControl.invalid ||
              lastNameControl.invalid ||
              emailControl.invalid
            "
            (click)="onSave()">
            Save
          </button>
        </div>
      </div>
    </div>
  `,
})
export class DialogEditProfileComponent {
  /** PROPERTIES **/
  protected readonly departments = [
    'Engineering',
    'Design',
    'Product',
    'Operations',
    'Finance',
  ];

  private readonly _managers: Manager[] = [
    { id: 1, name: 'Alice Müller', department: 'Engineering' },
    { id: 2, name: 'Bob Schmidt', department: 'Design' },
    { id: 3, name: 'Carol Jensen', department: 'Product' },
    { id: 4, name: 'David Park', department: 'Operations' },
  ];

  protected readonly firstNameControl = new FormControl(
    this.dialogRef.data?.firstName ?? '',
    Validators.required
  );
  protected readonly lastNameControl = new FormControl(
    this.dialogRef.data?.lastName ?? '',
    Validators.required
  );
  protected readonly emailControl = new FormControl(
    this.dialogRef.data?.email ?? '',
    Validators.required
  );
  protected readonly departmentControl = new FormControl<string | null>(
    this.dialogRef.data?.department ?? null
  );
  protected readonly managerControl = new FormControl<Manager | null>(null);

  protected readonly fetchManagers = (query: string | null) =>
    of(this._managers).pipe(
      delay(200),
      map(list =>
        list.filter(m =>
          m.name.toLowerCase().includes((query ?? '').toLowerCase())
        )
      )
    );

  protected readonly compareManagers = (a: Manager, b: Manager) =>
    a?.id === b?.id;

  constructor(
    private readonly dialogRef: DialogRefService<DialogEditProfileComponent>
  ) {}

  /** Widens the dialog to give more room — demonstrates updateSize(). */
  protected onExpand(): void {
    this.dialogRef.updateSize('900px');
  }

  protected onCancel(): void {
    this.dialogRef.close(null);
  }

  protected onSave(): void {
    this.dialogRef.close({
      firstName: this.firstNameControl.value,
      lastName: this.lastNameControl.value,
      email: this.emailControl.value,
      department: this.departmentControl.value,
      manager: this.managerControl.value,
    });
  }
}
